"use client";

import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { countryCodes } from "@/data/countryCodes";
import { ContactCitySelect } from "@/components/shared/ContactCitySelect";
import { ContactPhoneOtpModal } from "@/components/contact/ContactPhoneOtpModal";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { requestGetOtp } from "@/lib/authClient";
import {
  NAV_CITY_CHANGED_EVENT,
  readSelectedCityFromCookies,
  resolveSelectedNavCityClient,
} from "@/lib/navCityCookie";
import { createTicket } from "@/lib/notificationsApi";
import { GTMEvents, GTMSubEvents, PushDataToGTM } from "@/lib/gtm";

/** E.164 digits only (no +); matches footer `tel:+966112345678` for WhatsApp deep link */
const CONTACT_WHATSAPP_E164 = "+966920022776";

type ContactFormState = {
  name: string;
  email: string;
  phoneCode: (typeof countryCodes)[number]["code"];
  phoneNumber: string;
  cityId: number | string | null;
  cityName: string;
  description: string;
  howDidYouFindUs: string | null;
};

/** Backend expects numeric topic ids; values align with `<option value>` slugs below. */
const HOW_DID_YOU_FIND_US_IDS: Record<string, number> = {
  "general-inquiry": 1,
  "technical-support": 2,
  "billing-payments": 3,
  "account-login": 4,
  "sales-pricing": 5,
  "feedback-suggestions": 6,
  complaint: 7,
  "partnership-business": 8,
  "report-abuse": 9,
  other: 10,
};

function emptyContactForm(): ContactFormState {
  return {
    name: "",
    email: "",
    phoneCode: countryCodes[0].code,
    phoneNumber: "",
    cityId: null,
    cityName: "",
    description: "",
    howDidYouFindUs: null,
  };
}

export function splitPhoneByCountryCode(value: string | undefined): {
  phoneCode: (typeof countryCodes)[number]["code"];
  phoneNumber: string;
} {
  const raw = (value ?? "").trim();
  const codeMatch = countryCodes.find((c) => raw.startsWith(c.code));
  if (codeMatch) {
    return {
      phoneCode: codeMatch.code,
      phoneNumber: raw.slice(codeMatch.code.length).replace(/\D/g, ""),
    };
  }
  return {
    phoneCode: countryCodes[0].code,
    phoneNumber: raw.replace(/\D/g, ""),
  };
}

export default function ContactPage() {
  const { locale, t } = useLanguage();
  const { user, isLoggedIn, isReady } = useAuth();
  const langCode: "en" | "ar" = locale === "ar" ? "ar" : "en";

  const [formData, setFormData] = useState<ContactFormState>({
    name: "",
    email: "",
    phoneCode: countryCodes[0].code,
    phoneNumber: "",
    cityId: null,
    cityName: "",
    description: "",
    howDidYouFindUs: null,
  });

  const [formFeedback, setFormFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneOtpModalOpen, setPhoneOtpModalOpen] = useState(false);
  /** Snapshot when OTP is sent — verification API must use the same `mobileNumber`. */
  const [phoneOtpTargetE164, setPhoneOtpTargetE164] = useState("");
  const [guestPhoneVerifiedE164, setGuestPhoneVerifiedE164] = useState<string | null>(
    null
  );
  const [phoneVerifySending, setPhoneVerifySending] = useState(false);

  const contactFullPhone = useMemo(
    () =>
      `${formData.phoneCode}${formData.phoneNumber.replace(/\D/g, "")}`,
    [formData.phoneCode, formData.phoneNumber]
  );

  const guestPhoneMatchesVerified =
    !isLoggedIn &&
    guestPhoneVerifiedE164 != null &&
    guestPhoneVerifiedE164 === contactFullPhone;

  const resetFormPreservingNavCity = useCallback(() => {
    setFormData(() => {
      const next = emptyContactForm();
      const c = resolveSelectedNavCityClient();
      if (!c) return next;
      const cityName = locale === "ar" ? c.name : c.nameEn;
      return { ...next, cityId: c.id, cityName };
    });
  }, [locale]);

  /**
   * Same source as header: cookies, else legacy `localStorage` migrated to cookies.
   * Must run in layout effect (not transition/microtask) so the field shows the city on first paint.
   */
  useLayoutEffect(() => {
    const c = resolveSelectedNavCityClient();
    if (!c) return;
    setFormData((prev) => {
      if (prev.cityId !== null && prev.cityId !== c.id) return prev;
      const cityName = locale === "ar" ? c.name : c.nameEn;
      return { ...prev, cityId: c.id, cityName };
    });
  }, [locale]);

  /** Pre-fill phone from session when logged in; guests edit freely. */
  useEffect(() => {
    if (!isReady || !isLoggedIn || !user?.phone?.trim()) return;
    const parsed = splitPhoneByCountryCode(user.phone);
    setFormData((prev) => ({
      ...prev,
      phoneCode: parsed.phoneCode,
      phoneNumber: parsed.phoneNumber,
    }));
    setGuestPhoneVerifiedE164(null);
  }, [isReady, isLoggedIn, user?.phone]);

  /** Header city picker updates cookies + fires this — always mirror nav (don’t use the form-only guard from `useLayoutEffect`). */
  useEffect(() => {
    const onNavCity = () => {
      const c = readSelectedCityFromCookies();
      if (!c) return;
      setFormData((prev) => {
        const cityName = locale === "ar" ? c.name : c.nameEn;
        return { ...prev, cityId: c.id, cityName };
      });
    };
    window.addEventListener(NAV_CITY_CHANGED_EVENT, onNavCity);
    return () => window.removeEventListener(NAV_CITY_CHANGED_EVENT, onNavCity);
  }, [locale]);



  useEffect(() => {
    if (!formFeedback) return;
  
    const timer = setTimeout(() => {
      setFormFeedback(null);
    }, 5000);
  
    return () => clearTimeout(timer);
  }, [formFeedback]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormFeedback(null);
    if (!isLoggedIn) {
      if (!guestPhoneMatchesVerified) {
        setFormFeedback({
          type: "error",
          message: t("contact.phoneVerifyRequired"),
        });
        return;
      }
    }
    setIsSubmitting(true);
    const topic = formData.howDidYouFindUs?.trim() ?? "";
    try {
      const response = await createTicket({
        name: formData.name,
        email: formData.email,
        phone: formData.phoneNumber,
        phoneCode: formData.phoneCode,
        cityId: formData.cityId ?? "",
        cityName: formData.cityName,
        description: formData.description,
        howDidYouFindUs: HOW_DID_YOU_FIND_US_IDS[topic] ?? 0,
        latestOffer: true,
        termsAndConditions: true,
      });
      if (response.success) {
        const msg = t("contact.submitSuccess");
        setFormFeedback({ type: "success", message: msg });
        resetFormPreservingNavCity();
        PushDataToGTM(GTMEvents.DataLayerUpdate, {
          pageName: "CONTACT US",
          pageCategory: "CONTACT",
          eventCategory: "CONTACTUSLEADFORM",
          eventLabel:  "CONTACT US > CONTACTUSLEADSUBMIT"
        });
      } else {
        const msg = t("contact.submitError");
        setFormFeedback({ type: "error", message: msg });
      }
    } catch {
      setFormFeedback({
        type: "error",
        message: t("contact.submitErrorNetwork"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (name === "phoneNumber") {
      const digits = value.replace(/\D/g, "").slice(0, 15);
      setFormData((prev) => ({ ...prev, phoneNumber: digits }));
      if (!isLoggedIn) setGuestPhoneVerifiedE164(null);
      return;
    }
    if (name === "phoneCode") {
      setFormData((prev) => ({
        ...prev,
        phoneCode: value as ContactFormState["phoneCode"],
      }));
      if (!isLoggedIn) setGuestPhoneVerifiedE164(null);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleVerifyPhoneClick = async () => {
    setFormFeedback(null);
    const digits = formData.phoneNumber.replace(/\D/g, "");
    if (digits.length < 8) {
      setFormFeedback({
        type: "error",
        message: t("login.errorPhone"),
      });
      return;
    }
    setPhoneVerifySending(true);
    try {
      const { ok, message } = await requestGetOtp(contactFullPhone, langCode);
      if (!ok) {
        setFormFeedback({ type: "error", message });
        return;
      }
      setPhoneOtpTargetE164(contactFullPhone);
      setPhoneOtpModalOpen(true);
    } catch {
      setFormFeedback({
        type: "error",
        message: t("login.errorSendOtp"),
      });
    } finally {
      setPhoneVerifySending(false);
    }
  };

  return (
    <div className="bg-[#F4F4F4] min-h-screen w-full min-w-0 max-w-full">
      {/* Page Header */}
      <div className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8 w-full min-w-0">
          <nav className="flex items-center gap-2 text-sm text-[#6B7280] mb-4">
            <Link href="/" className="hover:text-[#BD2226] transition-colors">
              {t("nav.home")}
            </Link>
            <span>/</span>
            <span className="text-[#130F0E] font-medium">{t("contact.title")}</span>
          </nav>
          <h1 className="text-3xl font-bold text-[#130F0E] break-words">
            {t("contact.title")}
          </h1>
          <p className="text-[#6B7280] mt-2 max-w-2xl break-words">
            {t("contact.introFull")}
          </p>
        </div>
      </div>

      <div className="max-w-[1320px] mx-auto sm:px-6 px-4 md:py-12 py-6 w-full min-w-0">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 w-full min-w-0">
          {/* Contact Info */}
          <div className="min-w-0 space-y-8 lg:col-span-1">
            <div className="bg-white rounded-xl border border-[#E5E5E5] p-6 sm:p-8">
              <h2 className="text-xl font-bold text-[#130F0E] mb-6">
                {t("contact.getInTouch")}
              </h2>

              <div className="space-y-6">
                <div className="flex min-w-0 items-start gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-lg bg-[#BD2226]/10 flex items-center justify-center">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#DD0428"
                      strokeWidth="2"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#130F0E]">{t("contact.address")}</p>
                    <p className="text-[#6B7280] mt-1 break-words">
                      {t("contact.addressLine1")}
                      <br />
                      {t("contact.addressLine2")}
                    </p>
                  </div>
                </div>

                <div className="flex min-w-0 items-start gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-lg bg-[#BD2226]/10 flex items-center justify-center">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#DD0428"
                      strokeWidth="2"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#130F0E]">
                      {t("contact.complaints")}
                    </p>
                    <a
                      href="tel:8004420020"
                      className="text-[#BD2226] font-semibold mt-1 block break-all hover:underline"
                    >
                      800 442 0020
                    </a>
                  </div>
                </div>

                <div className="flex min-w-0 items-start gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-lg bg-[#BD2226]/10 flex items-center justify-center">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#DD0428"
                      strokeWidth="2"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#130F0E]">{t("contact.emailContactLabel")}</p>
                    <a
                      href="mailto:petrominexpress@petromin.com"
                      className="text-[#BD2226] mt-1 block break-all hover:underline"
                    >
                     petrominexpress@petromin.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-[#E5E5E5] p-6 sm:p-8">
              <h3 className="font-bold text-[#130F0E] mb-4">{t("contact.workingHours")}</h3>
              <div className="space-y-6 text-[#6B7280]">
                <div className="space-y-2 border-b border-[#E5E7EB] pb-4 last:border-0 last:pb-0">
                  <p className="text-sm font-semibold text-[#BD2226]">
                    {t("contact.petrominExpress")}
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex min-w-0 justify-between gap-3">
                      <span className="min-w-0 break-words">
                        {t("contact.hoursLabelMonThuSatSun")}
                      </span>
                      <span className="shrink-0 text-end font-medium text-[#130F0E]">
                        {t("contact.hoursExpressWeekdays")}
                      </span>
                    </div>
                    <div className="flex min-w-0 justify-between gap-3">
                      <span className="min-w-0 break-words">{t("contact.friday")}</span>
                      <span className="shrink-0 text-end font-medium text-[#130F0E]">
                        {t("contact.hoursExpressFriday")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 border-b border-[#E5E7EB] pb-4 last:border-0 last:pb-0">
                  <p className="text-sm font-semibold text-[#BD2226]">
                    {t("contact.petrominAutocare")}
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex min-w-0 justify-between gap-3">
                      <span className="min-w-0 break-words">
                        {t("contact.hoursLabelMonThuSatSun")}
                      </span>
                      <span className="shrink-0 text-end font-medium text-[#130F0E]">
                        {t("contact.hoursAutocareWeekdays")}
                      </span>
                    </div>
                    <div className="flex min-w-0 justify-between gap-3">
                      <span className="min-w-0 break-words">{t("contact.friday")}</span>
                      <span className="shrink-0 text-end font-medium text-[#130F0E]">
                        {t("contact.hoursClosed")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 border-b border-[#E5E7EB] pb-4 last:border-0 last:pb-0">
                  <p className="text-sm font-semibold text-[#BD2226]">
                    {t("contact.petrominBodyPaint")}
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex min-w-0 justify-between gap-3">
                      <span className="min-w-0 break-words">
                        {t("contact.hoursLabelMonThuSatSun")}
                      </span>
                      <span className="shrink-0 text-end font-medium text-[#130F0E]">
                        {t("contact.hoursBodyPaintWeekdays")}
                      </span>
                    </div>
                    <div className="flex min-w-0 justify-between gap-3">
                      <span className="min-w-0 break-words">{t("contact.friday")}</span>
                      <span className="shrink-0 text-end font-medium text-[#130F0E]">
                        {t("contact.hoursClosed")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-[#BD2226]">
                    {t("contact.petrominCommercialVehicles")}
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex min-w-0 justify-between gap-3">
                      <span className="min-w-0 break-words">
                        {t("contact.hoursLabelMonThuSatSun")}
                      </span>
                      <span className="shrink-0 text-end font-medium text-[#130F0E]">
                        {t("contact.hoursCommercialWeekdays")}
                      </span>
                    </div>
                    <div className="flex min-w-0 justify-between gap-3">
                      <span className="min-w-0 break-words">{t("contact.friday")}</span>
                      <span className="shrink-0 text-end font-medium text-[#130F0E]">
                        {t("contact.hoursClosed")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Link
              href="/find-workshop"
              onClick={() => {
                PushDataToGTM(GTMEvents.DataLayerUpdate, {
                  pageName: "CONTACT US",
                  pageCategory: "CONTACT",
                  eventCategory: GTMSubEvents.Click,
                  eventLabel:  "CONTACT US > FIND NEAREST CENTER",
                });
              }}
              className="flex min-w-0 w-full items-center justify-center gap-2 break-words bg-[#BD2226] px-2 text-center text-white font-semibold py-3.5 rounded-lg hover:bg-[#C00322] transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {t("contact.findCenter")}
            </Link>
          </div>

          {/* Contact Form */}
          <div className="min-w-0 lg:col-span-2">
            <div className="bg-white rounded-xl border border-[#E5E5E5] p-6 sm:p-8 md:p-10">
              <h2 className="text-2xl font-bold text-[#130F0E] mb-6">
                {t("contact.sendMessage")}
              </h2>

              <form
                id="contact-main-form"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                {formFeedback ? (
                  <div
                    role="alert"
                    className={
                      formFeedback.type === "success"
                        ? "rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900"
                        : "rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
                    }
                  >
                    {formFeedback.message}
                  </div>
                ) : null}
                <div className="grid min-w-0 grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="min-w-0">
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-[#130F0E] mb-2"
                    >
                      {t("contact.nameRequired")}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full min-w-0 max-w-full px-4 py-3 rounded-lg border border-[#E5E5E5] text-[#130F0E] focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20 focus:border-[#DD0428]"
                      placeholder={t("contact.namePlaceholder")}
                    />
                  </div>
                  <div className="min-w-0">
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-[#130F0E] mb-2"
                    >
                      {t("contact.emailRequired")}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full min-w-0 max-w-full px-4 py-3 rounded-lg border border-[#E5E5E5] text-[#130F0E] focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20 focus:border-[#DD0428]"
                      placeholder={t("contact.emailPlaceholder")}
                    />
                  </div>
                </div>

                <div className="grid min-w-0 grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-[#130F0E] mb-2">
                      {t("contact.phone")}*
                    </label>
                    <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-stretch">
                      <div className="flex min-w-0 flex-1 gap-2">
                        <select
                          id="contact-phone-country"
                          name="phoneCode"
                          value={formData.phoneCode}
                          onChange={handleChange}
                          disabled={isLoggedIn || phoneOtpModalOpen}
                          aria-disabled={isLoggedIn || phoneOtpModalOpen}
                          className="w-24 shrink-0 rounded-lg border border-[#E5E5E5] bg-white px-2 py-3 text-sm text-[#130F0E] focus:border-[#DD0428] focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20 disabled:cursor-not-allowed disabled:bg-[#F9FAFB] disabled:text-[#6B7280]"
                        >
                          {countryCodes.map((c) => (
                            <option key={`${c.code}-${c.country}`} value={c.code}>
                              {c.flag} {c.code}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          id="phoneNumber"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                          placeholder={t("contact.phoneNationalPlaceholder")}
                          autoComplete="tel-national"
                          required
                          disabled={isLoggedIn || phoneOtpModalOpen}
                          readOnly={isLoggedIn}
                          className="min-w-0 flex-1 rounded-lg border border-[#E5E5E5] px-4 py-3 text-[#130F0E] placeholder:text-[#9CA3AF] focus:border-[#DD0428] focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20 disabled:cursor-not-allowed disabled:bg-[#F9FAFB] disabled:text-[#6B7280]"
                        />
                      </div>
                      {!isLoggedIn ? (
                        <button
                          type="button"
                          onClick={handleVerifyPhoneClick}
                          disabled={
                            phoneVerifySending ||
                            guestPhoneMatchesVerified ||
                            phoneOtpModalOpen
                          }
                          aria-busy={phoneVerifySending}
                          className="shrink-0 rounded-lg border border-[#DD0428] bg-white px-4 py-3 text-sm font-semibold text-[#BD2226] transition-colors hover:bg-[#BD2226]/5 disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-[4.5rem]"
                        >
                          {phoneVerifySending
                            ? t("login.sending")
                            : guestPhoneMatchesVerified
                              ? t("contact.phoneVerified")
                              : t("contact.verifyPhone")}
                        </button>
                      ) : null}
                    </div>
                    {/* {!isLoggedIn ? (
                      <p className="mt-1.5 text-xs text-[#6B7280]">
                        {t("contact.phoneVerifyHint")}
                      </p>
                    ) : null} */}
                  </div>
                  <div className="min-w-0">
                    <label
                      htmlFor="contact-city-trigger"
                      className="block text-sm font-medium text-[#130F0E] mb-2"
                    >
                      {t("contact.city")}
                    </label>
                    <ContactCitySelect
                      cityId={formData.cityId}
                      cityName={formData.cityName}
                      onChange={(next) => {
                        setFormData((prev) => ({
                          ...prev,
                          cityId: next?.cityId ?? null,
                          cityName: next?.cityName ?? "",
                        }));
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="about"
                    className="block text-sm font-medium text-[#130F0E] mb-2"
                  >
                    {t("contact.aboutTopic")}*
                  </label>
                  <select
                    id="howDidYouFindUs"
                    name="howDidYouFindUs"  
                    value={formData.howDidYouFindUs ?? ""}
                    onChange={handleChange}
                    required
                    className="w-full min-w-0 max-w-full px-4 py-3 rounded-lg border border-[#E5E5E5] text-[#130F0E] focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20 focus:border-[#DD0428] bg-white"
                  >
                    <option value="">{t("contact.selectOption")}</option>
                    <option value="general-inquiry">{t("contact.reasonGeneralInquiry")}</option>
                    <option value="technical-support">
                      {t("contact.reasonTechnicalSupport")}
                    </option>
                    <option value="billing-payments">{t("contact.reasonBilling")}</option>
                    <option value="account-login">
                      {t("contact.reasonAccountLogin")}
                    </option>
                    <option value="sales-pricing">
                      {t("contact.reasonSalesPricing")}
                    </option>
                    <option value="feedback-suggestions">
                      {t("contact.reasonFeedback")}
                    </option>
                    <option value="complaint">{t("contact.reasonComplaint")}</option>
                    <option value="partnership-business">
                      {t("contact.reasonPartnership")}
                    </option>
                    <option value="report-abuse">
                      {t("contact.reasonReportAbuse")}
                    </option>
                    <option value="other">{t("contact.reasonOther")}</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-[#130F0E] mb-2"
                    >
                      {t("contact.messageRequired")}
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      required
                      rows={5}
                      value={formData.description}
                      onChange={handleChange}
                      className="w-full min-w-0 max-w-full px-4 py-3 rounded-lg border border-[#E5E5E5] text-[#130F0E] focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20 focus:border-[#DD0428] resize-none"
                      placeholder={t("contact.messagePlaceholder")}
                    />
                </div>
                <ContactPhoneOtpModal
                  open={phoneOtpModalOpen}
                  onClose={() => setPhoneOtpModalOpen(false)}
                  fullPhone={phoneOtpTargetE164}
                  onVerified={(verifiedE164) =>
                    setGuestPhoneVerifiedE164(verifiedE164)
                  }
                />

                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={
                      isSubmitting || (!isLoggedIn && !guestPhoneMatchesVerified)
                    }
                    aria-busy={isSubmitting}
                    className="w-full sm:w-auto rounded-lg bg-[#BD2226] px-10 py-3.5 font-semibold text-white transition-colors hover:bg-[#C00322] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? t("contact.sending") : t("contact.sendBtn")}
                  </button>
                  <p className="w-full min-w-0 text-xs leading-relaxed text-[#6B7280] text-balance break-words">
                    {t("contact.legalIntro")}
                    <Link
                      href="/terms-and-conditions"
                      onClick={() => {
                        PushDataToGTM(GTMEvents.DataLayerUpdate, {
                          pageName: "CONTACT US",
                          pageCategory: "CONTACT",
                          eventCategory: GTMSubEvents.Click,
                          eventLabel:  "CONTACT US > TERMS AND CONDITIONS",
                        });
                      }}
                      className="text-[#BD2226] underline underline-offset-2 hover:text-[#C00322]"
                    >
                      {t("contact.legalTermsLink")}
                    </Link>
                    {t("contact.legalMid")}
                    <Link
                      href="/privacy-policy"
                      onClick={() => {
                        PushDataToGTM(GTMEvents.DataLayerUpdate, {
                          pageName: "CONTACT US",
                          pageCategory: "CONTACT",
                          eventCategory: GTMSubEvents.Click,
                          eventLabel:  "CONTACT US > PRIVACY POLICY",
                        });
                      }}
                      className="text-[#BD2226] underline underline-offset-2 hover:text-[#C00322]"
                    >
                      {t("contact.legalPrivacyLink")}
                    </Link>
                    {t("contact.legalEnd")}
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-20 end-6 z-50">
        <div className="relative flex h-14 w-14 items-center justify-center">
          <span
            className="contact-whatsapp-ping pointer-events-none absolute -inset-3 rounded-full bg-[#25D366]/25 motion-safe:animate-ping motion-safe:[animation-duration:2.6s]"
            aria-hidden
          />
          <a
            href={`https://wa.me/${CONTACT_WHATSAPP_E164}`}
            onClick={() => {
              PushDataToGTM(GTMEvents.DataLayerUpdate, {
                pageName: "CONTACT US",
                pageCategory: "CONTACT",
                eventCategory: GTMSubEvents.Click,
                eventLabel:  "CONTACT US > WHATSAPP CLICK",
              });
            }}
            target="_blank"
            rel="noopener noreferrer"
            className="contact-whatsapp-fab relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_6px_28px_rgba(37,211,102,0.5)] transition-transform duration-200 hover:scale-110 hover:shadow-[0_14px_40px_rgba(37,211,102,0.55)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2"
            aria-label={t("contact.whatsappAria")}
          >
            <svg
              className="relative z-[1] h-8 w-8 drop-shadow-sm"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}
